import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as AutoIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

type ThemeMode = 'light' | 'dark' | 'auto';

const AppearanceTab: React.FC = () => {
  const [themeMode, setThemeMode] = useState<ThemeMode>('light');
  const [settings, setSettings] = useState({
    compactMode: false,
    showAnimations: true,
    highContrast: false,
    reduceMotion: false,
  });

  useEffect(() => {
    // Load saved theme preference
    const savedTheme = localStorage.getItem('themeMode') as ThemeMode;
    if (savedTheme) {
      setThemeMode(savedTheme);
    }
    
    // Load saved settings
    const savedSettings = localStorage.getItem('appearanceSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleThemeChange = (
    event: React.MouseEvent<HTMLElement>,
    newTheme: ThemeMode | null
  ) => {
    if (newTheme !== null) {
      setThemeMode(newTheme);
      localStorage.setItem('themeMode', newTheme);
      
      // Apply theme to document
      if (newTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else if (newTheme === 'light') {
        document.documentElement.classList.remove('dark');
      } else {
        // Auto mode - check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        if (prefersDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }
      
      toast.success(`Theme changed to ${newTheme} mode`);
    }
  };

  const handleSettingChange = (setting: keyof typeof settings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSettings = {
      ...settings,
      [setting]: event.target.checked,
    };
    setSettings(newSettings);
    localStorage.setItem('appearanceSettings', JSON.stringify(newSettings));
    toast.success(`${setting} setting updated`);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Appearance Settings
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Theme Mode
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose your preferred color theme
        </Typography>
        
        <ToggleButtonGroup
          value={themeMode}
          exclusive
          onChange={handleThemeChange}
          aria-label="Theme mode"
          sx={{ mb: 3 }}
        >
          <ToggleButton value="light" aria-label="Light mode">
            <LightModeIcon sx={{ mr: 1 }} />
            Light
          </ToggleButton>
          <ToggleButton value="dark" aria-label="Dark mode">
            <DarkModeIcon sx={{ mr: 1 }} />
            Dark
          </ToggleButton>
          <ToggleButton value="auto" aria-label="Auto mode">
            <AutoIcon sx={{ mr: 1 }} />
            Auto
          </ToggleButton>
        </ToggleButtonGroup>

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" gutterBottom>
          Display Options
        </Typography>
        
        <List>
          <ListItem>
            <ListItemText
              primary="Compact Mode"
              secondary="Reduce spacing and padding throughout the interface"
            />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.compactMode}
                    onChange={handleSettingChange('compactMode')}
                  />
                }
                label=""
              />
            </ListItemSecondaryAction>
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary="Show Animations"
              secondary="Enable smooth transitions and animations"
            />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.showAnimations}
                    onChange={handleSettingChange('showAnimations')}
                  />
                }
                label=""
              />
            </ListItemSecondaryAction>
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary="High Contrast"
              secondary="Increase contrast for better visibility"
            />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.highContrast}
                    onChange={handleSettingChange('highContrast')}
                  />
                }
                label=""
              />
            </ListItemSecondaryAction>
          </ListItem>
          
          <ListItem>
            <ListItemText
              primary="Reduce Motion"
              secondary="Minimize animations and transitions for accessibility"
            />
            <ListItemSecondaryAction>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.reduceMotion}
                    onChange={handleSettingChange('reduceMotion')}
                  />
                }
                label=""
              />
            </ListItemSecondaryAction>
          </ListItem>
        </List>
      </Paper>
    </Box>
  );
};

export default AppearanceTab;
